import React, { Component } from 'react';
import { Table, Button, Modal } from 'semantic-ui-react';
import { getSnapshot } from 'mobx-state-tree';
import { observer } from 'mobx-react';
import { componentContainerStyle, componentContentStyle } from '../../constants';
import Loading from '../Loading/Loading';
import MultiplyHeader from '../MultiplyHeader/MultiplyHeader';
import ErrorDisplay from '../ErrorDisplay/ErrorDisplay';
import AllUsersListModal from '../AllUsersListModal/AllUsersListModal';

const formStyles = {
    label: { display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '4px' },
    input: { width: '100%', padding: '8px 12px', fontSize: '14px', border: '1px solid rgba(34,36,38,.15)', borderRadius: '4px', fontFamily: 'inherit', boxSizing: 'border-box', margin: 0, outline: 'none' },
    select: { width: '100%', padding: '8px 12px', fontSize: '14px', border: '1px solid rgba(34,36,38,.15)', borderRadius: '4px', fontFamily: 'inherit', boxSizing: 'border-box', backgroundColor: '#fff', margin: 0 },
    btnPrimary: { padding: '10px 16px', backgroundColor: '#0784a7', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', marginRight: '8px', marginBottom: '8px', width: 189 },
    row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' },
    row4: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px', marginBottom: '16px' },
};

const blankUser = {
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    userType: '',
};

class UserManagement extends Component {
    constructor(props) {
        super(props);

        blankUser.reportsToId = props.usersStore.companyAdmins[0]?.userId || props.usersStore.currentUser.userId;

        this.state = {
            editIndex: -1,
            editUser: {},
            newUser: { ...blankUser },
            showPassword: false,
            error: '',
            showDeactivateCompanyAdmin: false,
            showAllUsersModal: false,
            selectedUsers: [],
            showRemoveUserModal: false,
            showDeleteUserModal: false,
            userToRemove: null,
            userToDelete: null,
            showRecoverExistingUserModal: false,
            existingUserToRecover: null,
        };
    }

    onEditClick(i) {
        const { companyUsers } = this.props.usersStore;
        this.setState({ editIndex: i, editUser: getSnapshot(companyUsers[i]) });
    }

    onCancelClick(i) {
        this.setState({ editIndex: -1 });
    }

    onGridDropdownChange(data, i) {
        let user = { ...this.state.editUser };

        user[data.name] = data.value;

        this.setState({ editUser: user });
    }

    onCheckboxChanged(data, i) {
        let user = { ...this.state.editUser };

        user[data.name] = data.checked;

        this.setState({ editUser: user });
    }

    onGridTextChange(event) {
        let user = { ...this.state.editUser };

        user[event.target.name] = event.target.value;

        this.setState({ editUser: user });
    }

    onNewUserDropdownChange = data => {
        const { newUser } = this.state;

        newUser[data.name] = data.value;
        this.setState({ newUser });
    };

    onNewUserTextChange = (event) => {
        const { newUser } = this.state;

        newUser[event.target.name] = event.target.value;
        this.setState({ newUser });
    };

    onNewUserReadOnlyChange = (_, data) => {
        const { newUser } = this.state;

        newUser.isReadOnly = data.checked;

        this.setState({ newUser });
    };

    onEditUserReadOnlyChange = (_, data) => {
        let user = { ...this.state.editUser };

        user.isReadOnly = data.checked;

        this.setState({ editUser: user });
    };

    async onSaveClick(i) {
        const userSnapshot = this.state.editUser;

        this.setState({ error: '' });

        if (userSnapshot.userType === 'Company Admin' && !userSnapshot.active) {
            this.setState({ showDeactivateCompanyAdmin: true });
        } else {
            const user = this.props.usersStore.companyUsers[i];
            try {
                await user.updateFromSnapshot(userSnapshot);

                this.setState({ editIndex: -1 });
            } catch (error) {
                this.setState({ error: error.message });
            }
        }
    }

    saveNewUser = async () => {
        const { usersStore, company } = this.props;
        const newUser = { ...this.state.newUser, companyIds: [company.id] };

        if (newUser.userType === 'Executive' || newUser.userType === 'Senior Manager' || newUser.userType === 'Company Admin') {
            delete newUser.reportsToId;
        }

        this.setState({ error: '' });

        try {
            await usersStore.signUpUserFromSnapshot(newUser);
            // await company.chargeForUser();
            this.setState({ newUser: { ...blankUser } });
        } catch (error) {
            if (typeof error.message === 'string' && error.message.includes('User already exists')) {
                try {
                    const existing = await usersStore.lookupUserByEmail(newUser.email);
                    if (existing) {
                        this.setState({ showRecoverExistingUserModal: true, existingUserToRecover: existing });
                        return;
                    }
                } catch (lookupError) {
                    console.log('lookupUserByEmail failed:', lookupError);
                }
            }
            this.setState({ error: error.message });
        }
    };

    closeRecoverExistingUserModal = () => {
        this.setState({ showRecoverExistingUserModal: false, existingUserToRecover: null });
    };

    confirmRecoverExistingUser = async () => {
        const { usersStore, company } = this.props;
        const { existingUserToRecover, newUser } = this.state;
        if (!existingUserToRecover) {
            this.closeRecoverExistingUserModal();
            return;
        }

        this.setState({ error: '' });
        try {
            await usersStore.attachExistingUserToCompany(
                existingUserToRecover,
                company.id,
                newUser.reportsToId
            );
            this.closeRecoverExistingUserModal();
            this.setState({ newUser: { ...blankUser } });
        } catch (e) {
            this.closeRecoverExistingUserModal();
            this.setState({ error: e.message });
        }
    };

    formatBoolean(value) {
        return value ? 'Yes' : 'No';
    }

    userTypeOptions = [
        {
            key: 'Company Admin',
            text: 'Company Admin',
            value: 'Company Admin'
        },
        {
            key: 'Executive',
            text: 'Executive',
            value: 'Executive'
        },
        {
            key: 'Senior Manager',
            text: 'Senior Manager',
            value: 'Senior Manager'
        },
        {
            key: 'Sales Manager',
            text: 'Sales Manager',
            value: 'Sales Manager'
        },
        {
            key: 'Sales Member',
            text: 'Sales Member',
            value: 'Sales Member'
        }
    ];

    userFromId = (id) => {
        const { usersStore } = this.props;
        const user = usersStore.companyUsers.find(u => u.userId === id);
        return user ? `${user.firstName} ${user.lastName}` : '';
    };

    potentialManagers = (editUser) => {
        const { usersStore } = this.props;

        const salesManagers = usersStore.salesManagers.map(m => {
            return {
                key: m.userId,
                text: `${m.firstName} ${m.lastName}`,
                value: m.userId
            };
        });

        const seniorManagers = usersStore.seniorManagers.map(m => {
            return {
                key: m.userId,
                text: `${m.firstName} ${m.lastName}`,
                value: m.userId
            };
        });

        const companyAdmins = usersStore.companyAdmins.map(m => {
            return {
                key: m.userId,
                text: `${m.firstName} ${m.lastName}`,
                value: m.userId
            };
        });

        const executives = usersStore.executives.map(m => {
            return {
                key: m.userId,
                text: `${m.firstName} ${m.lastName}`,
                value: m.userId
            };
        });

        switch (editUser.userType) {
            case 'Executive':
            case 'Enterprise Executive':
            case 'Company Admin':
            case 'admin':
                return [];
            case 'Senior Manager':
                return [...companyAdmins, ...executives];
            case 'Sales Manager':
                return [...seniorManagers, ...companyAdmins, ...executives];
            default:
                return [...salesManagers, ...seniorManagers, ...companyAdmins, ...executives];
        }
    };

    saveButtonEnabled = () => {
        const { newUser } = this.state;
        return newUser.firstName
            && newUser.lastName
            && newUser.email
            && newUser.userType
            && newUser.email?.trim() === newUser.confirmEmail?.trim();
    };

    togglePassword = () => {
        this.setState(prevState => ({ showPassword: !prevState.showPassword }));
    };

    isCurrentUser = (user) => {
        const { currentUser } = this.props.usersStore;

        return currentUser.userId === user.userId;
    };

    getRankForUser = (user) => {
        switch (user.userType) {
            case 'admin':
                return 5;
            case 'Company Admin':
                return 4;
            case 'Executive':
                return 3;
            case 'Senior Manager':
                return 2.5;
            case 'Sales Manager':
                return 2;
            default:
                return 0;
        }
    };

    hasHigherRank = (user) => {
        const { currentUser } = this.props.usersStore;
        return this.getRankForUser(currentUser) >= this.getRankForUser(user);
    };

    isCompanyAdmin = (user) => {
        return user.userType === 'Company Admin';
    };

    moreThanOneCompanyAdmin = () => {
        return this.props.usersStore.companyUsers.filter(user => user.isCompanyAdmin).length > 1;
    };

    isAdmin = (user) => {
        return user.isAdmin;
    };

    canChangeUserType = (user) => {
        return !this.isCompanyAdmin(user) && !this.isCurrentUser(user);
    };

    canChangeActiveStatus = (user) => {
        const { currentUser } = this.props.usersStore;

        return (!this.isCompanyAdmin(user) || currentUser.isAdmin) && !this.isCurrentUser(user);
    };

    canChangeReportsTo = (user) => {
        return (!this.isCompanyAdmin(user) || this.moreThanOneCompanyAdmin()) && !this.isCurrentUser(user);
    };

    deactivateCompanyAdmin = async (user) => {
        const { usersStore } = this.props;

        if (user.isCompanyAdmin) {
            return;
        }

        await usersStore.deactivateCompanyAdmin(user.companyIds[0]);
    };

    openAllUsersModal = () => {
        this.setState({ showAllUsersModal: true });
    };

    onAddFromUserModal = async (selectedUsers) => {
        const promises = [];
        for (const user of selectedUsers) {
            promises.push(user.addUserToCompany(this.props.company.id));
        }

        await Promise.all(promises);
        await this.props.usersStore.loadCompanyUsers(this.props.company.id);
        this.setState({ showAllUsersModal: false });
    };

    closeAllUsersModal = () => {
        this.setState({ showAllUsersModal: false });
    };

    openRemoveUserModal = (user) => {
        this.setState({ showRemoveUserModal: true, userToRemove: user });
    };

    closeRemoveUserModal = () => {
        this.setState({ showRemoveUserModal: false, userToRemove: null });
    };

    confirmRemoveUser = async () => {
        const { userToRemove } = this.state;
        this.closeRemoveUserModal();
        await this.removeFromCompany(userToRemove);
    };

    openDeleteUserModal = (user) => {
        this.setState({ showDeleteUserModal: true, userToDelete: user });
    };

    closeDeleteUserModal = () => {
        this.setState({ showDeleteUserModal: false, userToDelete: null });
    };

    confirmDeleteUser = async () => {
        const { userToDelete } = this.state;
        const u = this.props.usersStore.companyUsers.find(u => u.userId === userToDelete.userId);
        this.closeDeleteUserModal();
        try {
            this.setState({ error: '' });
            await u.delete();
            await this.props.usersStore.loadCompanyUsers(this.props.company.id);
        } catch (e) {
            this.setState({ error: e.message });
        }
    };

    renderNewUserForm() {
        const { newUser, showDeactivateCompanyAdmin, editUser } = this.state;
        const { usersStore } = this.props;
        const emailError = !!newUser.confirmEmail && newUser.confirmEmail !== newUser.email;
        const showReportsTo = newUser.userType !== 'Executive' && newUser.userType !== 'Company Admin';
        const showReadOnly = newUser.userType === 'Executive' || newUser.userType === 'Senior Manager' || newUser.userType === 'Sales Manager';

        return (
            <div style={{ position: 'relative', opacity: usersStore.isLoading ? 0.5 : 1, pointerEvents: usersStore.isLoading ? 'none' : 'auto' }}>
                <Modal open={showDeactivateCompanyAdmin}>
                    <Modal.Header>Deactivate Company Admin</Modal.Header>
                    <Modal.Content>
                        <p>Are you sure you want to deactivate the company admin?  This will deactive all users for the company.</p>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button
                            onClick={() => {
                                let user = { ...this.state.editUser };
                                user.active = true;
                                this.setState({ editUser: user, showDeactivateCompanyAdmin: false });
                            }}
                            negative
                        >
                            No
                        </Button>
                        <Button
                            loading={usersStore.isLoading}
                            onClick={async () => {
                                await this.deactivateCompanyAdmin(editUser);
                                this.setState({ showDeactivateCompanyAdmin: false, editIndex: -1 });
                            }}
                            positive
                        >
                            Yes
                        </Button>
                    </Modal.Actions>
                </Modal>

                {/* Row 1: First Name, Last Name */}
                <div style={formStyles.row2}>
                    <div>
                        <label style={formStyles.label}>First Name *</label>
                        <input data-testid="new-first-name" style={formStyles.input} placeholder="First Name" name="firstName" value={newUser.firstName} onChange={this.onNewUserTextChange} />
                    </div>
                    <div>
                        <label style={formStyles.label}>Last Name *</label>
                        <input data-testid="new-last-name" style={formStyles.input} placeholder="Last Name" name="lastName" value={newUser.lastName} onChange={this.onNewUserTextChange} />
                    </div>
                </div>

                {/* Row 2: Email, Type, Reports To / Read Only */}
                <div style={formStyles.row4}>
                    <div>
                        <label style={formStyles.label}>Email *</label>
                        <input data-testid="new-email" style={formStyles.input} placeholder="Email" name="email" value={newUser.email} onChange={this.onNewUserTextChange} />
                    </div>
                    <div>
                        <label style={formStyles.label}>Type *</label>
                        <select data-testid="new-type" style={formStyles.select} value={newUser.userType} onChange={(e) => this.onNewUserDropdownChange({ name: 'userType', value: e.target.value })}>
                            <option value="">Select Type</option>
                            {this.userTypeOptions.map(opt => <option key={opt.key} value={opt.value}>{opt.text}</option>)}
                        </select>
                    </div>
                    <div>
                        {showReportsTo && (
                            <>
                                <label style={formStyles.label}>Reports To *</label>
                                <select data-testid="new-reports-to" style={formStyles.select} value={newUser.reportsToId || ''} onChange={(e) => this.onNewUserDropdownChange({ name: 'reportsToId', value: e.target.value })}>
                                    <option value="">Select Manager</option>
                                    {this.potentialManagers(newUser).map(opt => <option key={opt.key} value={opt.value}>{opt.text}</option>)}
                                </select>
                            </>
                        )}
                        {showReadOnly && (
                            <>
                                <label style={formStyles.label}>Read Only Access</label>
                                <div style={{ marginTop: '8px' }}>
                                    <label style={{ cursor: 'pointer', fontSize: '14px' }}>
                                        <input type="checkbox" checked={newUser.isReadOnly || false} onChange={(e) => this.onNewUserReadOnlyChange(e, { checked: e.target.checked })} />
                                        {' '}{newUser.isReadOnly ? 'Yes' : 'No'}
                                    </label>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Row 3: Confirm Email */}
                <div style={formStyles.row4}>
                    <div>
                        <label style={formStyles.label}>Confirm Email *</label>
                        <input data-testid="new-confirm-email" style={{ ...formStyles.input, borderColor: emailError ? '#db2828' : undefined }} placeholder="Confirm Email" name="confirmEmail" value={newUser.confirmEmail} onChange={this.onNewUserTextChange} />
                        {emailError && <div style={{ color: '#db2828', fontSize: '12px', marginTop: '4px' }}>Emails are different.</div>}
                    </div>
                </div>

                {/* Buttons */}
                <div style={{ marginBottom: '16px' }}>
                    <button style={{ ...formStyles.btnPrimary, opacity: !this.saveButtonEnabled() ? 0.5 : 1, cursor: !this.saveButtonEnabled() ? 'not-allowed' : 'pointer' }} onClick={this.saveNewUser} disabled={!this.saveButtonEnabled()}>
                        Save New User
                    </button>
                    <button style={formStyles.btnPrimary} onClick={this.openAllUsersModal}>
                        Add Existing User
                    </button>
                </div>

                {/* Billing message */}
                <div style={{ padding: '12px 16px', backgroundColor: '#f0f7fa', border: '1px solid #a9d5de', borderRadius: '4px', color: '#276f86', fontSize: '14px' }}>
                    You are billed $25 monthly for each active user.
                </div>
            </div>
        );
    }

    renderEditButtons(editIndex, user, i) {
        const { currentUser } = this.props.usersStore;

        if (!this.isAdmin(currentUser) && !this.isCompanyAdmin(currentUser) && this.isCompanyAdmin(user)) {
            return null;
        }

        if (!this.hasHigherRank(user)) {
            return null;
        }

        const iconBtn = { padding: '6px 10px', fontSize: '13px', border: '1px solid rgba(34,36,38,.15)', borderRadius: '4px', cursor: 'pointer', background: '#fff', marginRight: '4px' };

        if (editIndex !== i) {
            return <button style={{ ...iconBtn, color: '#0784a7' }} data-testid={`edit-${i}`} onClick={() => this.onEditClick(i)}>Edit</button>;
        }

        return <div>
            <button style={{ ...iconBtn, color: '#0784a7' }} data-testid={`save-${i}`} onClick={() => this.onSaveClick(i)}>Save</button>
            <button style={{ ...iconBtn, color: '#db2828' }} data-testid={`cancel-${i}`} onClick={() => this.onCancelClick(i)}>Cancel</button>
        </div>;
    }

    async removeFromCompany(user) {
        const u = this.props.usersStore.companyUsers.find(u => u.userId === user.userId);
        await u.removeUserFromCompany(this.props.company.id);
        await this.props.usersStore.loadCompanyUsers(this.props.company.id);
    }

    renderTableRow(user, i, editIndex, editUser) {
        const reportsToOptions = this.potentialManagers(user);
        const cellInput = { width: '100%', padding: '6px 8px', fontSize: '13px', border: '1px solid rgba(34,36,38,.15)', borderRadius: '4px', fontFamily: 'inherit', boxSizing: 'border-box', margin: 0 };
        const cellSelect = { ...cellInput, backgroundColor: '#fff' };
        const actionBtn = { padding: '5px 10px', fontSize: '12px', border: '1px solid rgba(34,36,38,.15)', borderRadius: '4px', cursor: 'pointer', background: '#fff' };

        return (<Table.Row key={`user_row_${i}`} style={{ overflow: 'visible' }}>
            <Table.Cell>
                {this.renderEditButtons(editIndex, user, i)}
            </Table.Cell>
            <Table.Cell>
                {editIndex === i && this.canChangeActiveStatus(user) ?
                    <label style={{ cursor: 'pointer' }}>
                        <input type="checkbox" data-testid={`edit-active-${i}`} checked={editUser.active} onChange={(e) => this.onCheckboxChanged({ name: 'active', checked: e.target.checked }, i)} />
                        {' '}{editUser.active ? 'Yes' : 'No'}
                    </label>
                    : this.formatBoolean(user.active)}
            </Table.Cell>
            <Table.Cell>{editIndex !== i ?
                user.firstName :
                <input style={cellInput} data-testid={`edit-first-name-${i}`} name="firstName" onChange={(e) => this.onGridTextChange(e, i)} placeholder='First Name' value={editUser.firstName} />}
            </Table.Cell>
            <Table.Cell>{editIndex !== i ?
                user.lastName :
                <input style={cellInput} data-testid={`edit-last-name-${i}`} name="lastName" onChange={(e) => this.onGridTextChange(e, i)} placeholder='Last Name' value={editUser.lastName} />}
            </Table.Cell>
            <Table.Cell>{user.email}</Table.Cell>
            <Table.Cell>{editIndex === i && this.canChangeUserType(user) ?
                <select style={cellSelect} data-testid={`edit-type-${i}`} value={editUser.userType} onChange={(e) => this.onGridDropdownChange({ name: 'userType', value: e.target.value }, i)}>
                    <option value="">Select Type</option>
                    {this.userTypeOptions.map(opt => <option key={opt.key} value={opt.value}>{opt.text}</option>)}
                </select>
                : user.userType}
            </Table.Cell>
            <Table.Cell>{editIndex === i && this.canChangeReportsTo(user) ?
                <select style={cellSelect} data-testid={`edit-report-${i}`} value={editUser.reportsToId || ''} onChange={(e) => this.onGridDropdownChange({ name: 'reportsToId', value: e.target.value }, i)}>
                    <option value="">Select Manager</option>
                    {reportsToOptions.map(opt => <option key={opt.key} value={opt.value}>{opt.text}</option>)}
                </select>
                : this.userFromId(user.reportsToId)}
            </Table.Cell>
            <Table.Cell>
                {editIndex === i && (user.userType === 'Executive' || user.userType === 'Senior Manager' || user.userType === 'Sales Manager') ?
                    <label style={{ cursor: 'pointer' }}>
                        <input type="checkbox" checked={editUser.isReadOnly || false} onChange={(e) => this.onEditUserReadOnlyChange(e, { checked: e.target.checked })} />
                        {' '}{editUser.isReadOnly ? 'Yes' : 'No'}
                    </label> :
                    (user.userType === 'Executive' || user.userType === 'Senior Manager' || user.userType === 'Sales Manager') ? this.formatBoolean(user.isReadOnly) : null}
            </Table.Cell>
            <Table.Cell>
                {user.companyIds.length > 1 ? <button style={actionBtn} onClick={() => this.openRemoveUserModal(user)}>Remove</button> : null}
            </Table.Cell>
            <Table.Cell>
                {!this.isCurrentUser(user) && <button style={actionBtn} onClick={() => this.openDeleteUserModal(user)}>Delete</button>}
            </Table.Cell>
        </Table.Row>);
    }

    renderTableRows() {
        const companyUserClones = this.props.usersStore.companyUsers.map(u => getSnapshot(u));
        const { editIndex, editUser } = this.state;

        return (<Table.Body style={{ overflow: 'visible' }}>
            {companyUserClones.map((user, i) =>
                this.renderTableRow(user, i, editIndex, editUser)
            )}
        </Table.Body>);
    }

    renderRemoveUserModal() {
        const { showRemoveUserModal, userToRemove } = this.state;
        return (
            <Modal open={showRemoveUserModal} onClose={this.closeRemoveUserModal}>
                <Modal.Header>Remove User</Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to remove {userToRemove?.firstName} from the company?</p>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={this.closeRemoveUserModal} negative>No</Button>
                    <Button onClick={this.confirmRemoveUser} positive>Yes</Button>
                </Modal.Actions>
            </Modal>
        );
    }

    renderDeleteUserModal() {
        const { showDeleteUserModal, userToDelete } = this.state;
        return (
            <Modal open={showDeleteUserModal} onClose={this.closeDeleteUserModal}>
                <Modal.Header>Delete User</Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to delete {userToDelete?.firstName} from Multiply?</p>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={this.closeDeleteUserModal} negative>No</Button>
                    <Button onClick={this.confirmDeleteUser} positive>Yes</Button>
                </Modal.Actions>
            </Modal>
        );
    }

    renderRecoverExistingUserModal() {
        const { showRecoverExistingUserModal, existingUserToRecover } = this.state;
        const { company, usersStore } = this.props;
        if (!existingUserToRecover) return null;
        const fullName = `${existingUserToRecover.firstName} ${existingUserToRecover.lastName}`.trim();
        const alreadyInCompany = (existingUserToRecover.companyIds || []).includes(company.id);
        return (
            <Modal open={showRecoverExistingUserModal} onClose={this.closeRecoverExistingUserModal}>
                <Modal.Header>User Already Exists</Modal.Header>
                <Modal.Content>
                    {alreadyInCompany ? (
                        <p>
                            {fullName} ({existingUserToRecover.email}) is already a member of {company.name}.
                            They should appear in the user list — try refreshing.
                        </p>
                    ) : existingUserToRecover.isOrphan ? (
                        <p>
                            {fullName} ({existingUserToRecover.email}) exists in Multiply but is not currently
                            attached to any company. Add them to {company.name}?
                        </p>
                    ) : (
                        <p>
                            {fullName} ({existingUserToRecover.email}) already exists in Multiply.
                            Add them to {company.name}?
                        </p>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={this.closeRecoverExistingUserModal} negative>Cancel</Button>
                    {!alreadyInCompany && (
                        <Button
                            loading={usersStore.isLoading}
                            onClick={this.confirmRecoverExistingUser}
                            positive
                        >
                            Add to {company.name}
                        </Button>
                    )}
                </Modal.Actions>
            </Modal>
        );
    }

    render() {
        const { usersStore } = this.props;
        const { error, showAllUsersModal } = this.state;
        return (
            <div style={componentContainerStyle}>
                <MultiplyHeader text="User Management" />
                <div style={componentContentStyle}>
                    {this.renderNewUserForm()}
                    <ErrorDisplay message={error} />
                    <Loading active={usersStore.companyUsers.some(u => u.isLoading)} >
                        <Table celled width={13} textAlign="center" striped style={{ overflow: 'visible' }}>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell width={1} />
                                    <Table.HeaderCell width={1}>
                                        Active
                                    </Table.HeaderCell>
                                    <Table.HeaderCell width={2}>
                                        First Name
                                    </Table.HeaderCell>
                                    <Table.HeaderCell width={2}>
                                        Last Name
                                    </Table.HeaderCell>
                                    <Table.HeaderCell width={2}>
                                        Email
                                    </Table.HeaderCell>
                                    <Table.HeaderCell width={2}>
                                        Type
                                    </Table.HeaderCell>
                                    <Table.HeaderCell width={2}>
                                        Reports To
                                    </Table.HeaderCell>
                                    <Table.HeaderCell width={1}>
                                        Read Only Access
                                    </Table.HeaderCell>
                                    <Table.HeaderCell width={1}>
                                        Remove From Company
                                    </Table.HeaderCell>
                                    <Table.HeaderCell width={1}>
                                        Delete From Multiply
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            {this.renderTableRows()}
                        </Table>
                    </Loading>
                    <AllUsersListModal
                        usersStore={usersStore}
                        open={showAllUsersModal}
                        onClose={this.closeAllUsersModal}
                        onAdd={this.onAddFromUserModal}
                        currentCompanyId={this.props.company?.id}
                    />
                    {this.renderRemoveUserModal()}
                    {this.renderDeleteUserModal()}
                    {this.renderRecoverExistingUserModal()}
                </div>
            </div>
        );
    }
}

export default observer(UserManagement);